import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarPlus } from "lucide-react";

export const Reveal = ({ children, delay = 0, className = "" }) => {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    el.classList.add("revealed");
                    obs.disconnect();
                }
            },
            { threshold: 0.12 },
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return (
        <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>
            {children}
        </div>
    );
};

export const MaskedLine = ({ children, delay = 0, className = "", innerClass = "", inView = false }) => {
    const anim = { y: 0 };
    const transition = { delay, duration: 1.15, ease: [0.22, 1, 0.36, 1] };
    return (
        <span className={`block overflow-hidden ${className}`}>
            {inView ? (
                <motion.span
                    className={`block ${innerClass}`}
                    initial={{ y: "115%" }}
                    whileInView={anim}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={transition}
                >
                    {children}
                </motion.span>
            ) : (
                <motion.span className={`block ${innerClass}`} initial={{ y: "115%" }} animate={anim} transition={transition}>
                    {children}
                </motion.span>
            )}
        </span>
    );
};

export const Magnetic = ({ children, strength = 16 }) => {
    const ref = useRef(null);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    return (
        <div
            ref={ref}
            className="inline-block"
            onMouseMove={(e) => {
                const r = ref.current.getBoundingClientRect();
                setPos({
                    x: ((e.clientX - r.left) / r.width - 0.5) * strength,
                    y: ((e.clientY - r.top) / r.height - 0.5) * strength,
                });
            }}
            onMouseLeave={() => setPos({ x: 0, y: 0 })}
        >
            <motion.div animate={{ x: pos.x, y: pos.y }} transition={{ type: "spring", stiffness: 200, damping: 16, mass: 0.6 }}>
                {children}
            </motion.div>
        </div>
    );
};

export const GlowOrbs = () => (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="orb orb-1 w-[26rem] h-[26rem] -top-28 -left-24" style={{ background: "rgba(165,28,48,0.16)" }} />
        <div className="orb orb-2 w-[22rem] h-[22rem] top-1/3 -right-24" style={{ background: "rgba(201,162,75,0.09)" }} />
    </div>
);

export const SectionHead = ({ eyebrow, title, sub, center = true, className = "" }) => (
    <Reveal className={`${center ? "text-center" : ""} ${className}`}>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h2 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6 text-[var(--ivory)]">
            {title}
        </h2>
        {sub && (
            <p className={`mt-6 text-base md:text-lg text-muted-luxe leading-relaxed max-w-2xl ${center ? "mx-auto" : ""}`}>
                {sub}
            </p>
        )}
    </Reveal>
);

export const GoldBtn = ({ to, children, variant = "gold", testid, className = "", onClick, type = "button", arrow = true }) => {
    const cls = `btn btn-${variant} ${className}`;
    const inner = (
        <>
            <span>{children}</span>
            {arrow && <ArrowRight size={13} strokeWidth={1.5} />}
        </>
    );
    if (to) {
        return (
            <Link to={to} data-testid={testid} className={cls}>
                {inner}
            </Link>
        );
    }
    return (
        <button type={type} onClick={onClick} data-testid={testid} className={cls}>
            {inner}
        </button>
    );
};

export const Counter = ({ to, suffix = "", duration = 1800 }) => {
    const ref = useRef(null);
    const [val, setVal] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        let raf;
        const obs = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                obs.disconnect();
                const start = performance.now();
                const tick = (now) => {
                    const p = Math.min((now - start) / duration, 1);
                    const eased = 1 - Math.pow(1 - p, 4);
                    setVal(Math.round(to * eased));
                    if (p < 1) raf = requestAnimationFrame(tick);
                };
                raf = requestAnimationFrame(tick);
            },
            { threshold: 0.4 },
        );
        obs.observe(el);
        return () => {
            obs.disconnect();
            cancelAnimationFrame(raf);
        };
    }, [to, duration]);
    return (
        <span ref={ref}>
            {val.toLocaleString("en-IN")}
            {suffix}
        </span>
    );
};

export const Marquee = ({ items, className = "", reverse = false, editorial = false, slow = false }) => {
    const row = (key) => (
        <div key={key} className="flex shrink-0 items-center">
            {items.map((item, i) => (
                <span key={`${key}-${i}`} className="flex items-center">
                    <span className={`marquee-item px-8 text-xs tracking-[0.4em] uppercase text-[var(--champagne)]/80 whitespace-nowrap`}>
                        {item}
                    </span>
                    <span className="w-1.5 h-1.5 rotate-45 bg-[var(--gold)]/60 shrink-0" />
                </span>
            ))}
        </div>
    );
    return (
        <div className={`overflow-hidden border-y hairline py-5 ${editorial ? "marquee-editorial py-7" : ""} ${className}`} data-testid="marquee-strip">
            <div className={`marquee-track ${reverse ? "marquee-reverse" : ""} ${slow || editorial ? "marquee-slow" : ""}`}>
                {row("a")}
                {row("b")}
            </div>
        </div>
    );
};

export const Countdown = ({ compact = false }) => {
    const target = useRef(new Date("2027-01-29T09:00:00+05:30").getTime());
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);
    const diff = Math.max(0, target.current - now);
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    const cells = [
        { v: d, l: "Days" },
        { v: h, l: "Hours" },
        { v: m, l: "Minutes" },
        { v: s, l: "Seconds" },
    ];
    return (
        <div className={`flex ${compact ? "gap-4" : "gap-3 sm:gap-6"}`} data-testid="event-countdown">
            {cells.map((c) => (
                <div key={c.l} className="text-center">
                    <div
                        className={`font-display gold-text font-semibold ${
                            compact ? "text-lg" : "text-3xl sm:text-5xl"
                        } tabular-nums`}
                    >
                        {String(c.v).padStart(2, "0")}
                    </div>
                    <div className="text-[0.58rem] tracking-[0.3em] uppercase text-muted-luxe mt-1">{c.l}</div>
                </div>
            ))}
        </div>
    );
};

export const Particles = ({ density = 60, className = "" }) => {
    const ref = useRef(null);
    useEffect(() => {
        const canvas = ref.current;
        if (!canvas || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const ctx = canvas.getContext("2d");
        let raf;
        let w, h;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const resize = () => {
            w = canvas.offsetWidth;
            h = canvas.offsetHeight;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();
        window.addEventListener("resize", resize);
        const parts = Array.from({ length: density }, () => ({
            x: Math.random(),
            y: Math.random(),
            r: Math.random() * 1.6 + 0.4,
            vy: Math.random() * 0.00045 + 0.00012,
            vx: (Math.random() - 0.5) * 0.0002,
            tw: Math.random() * Math.PI * 2,
            ts: Math.random() * 0.02 + 0.006,
        }));
        const draw = () => {
            ctx.clearRect(0, 0, w, h);
            for (const p of parts) {
                p.y -= p.vy;
                p.x += p.vx;
                p.tw += p.ts;
                if (p.y < -0.02) {
                    p.y = 1.02;
                    p.x = Math.random();
                }
                const a = 0.25 + Math.sin(p.tw) * 0.25;
                ctx.beginPath();
                ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(232, 207, 143, ${Math.max(a, 0.05)})`;
                ctx.fill();
            }
            raf = requestAnimationFrame(draw);
        };
        raf = requestAnimationFrame(draw);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
        };
    }, [density]);
    return <canvas ref={ref} className={`absolute inset-0 w-full h-full pointer-events-none ${className}`} aria-hidden="true" />;
};

export const PageHero = ({ eyebrow, title, sub, children, testid }) => (
    <section className="relative pt-36 pb-16 md:pt-48 md:pb-24 overflow-hidden noise" data-testid={testid}>
        <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[70rem] h-[40rem] rounded-full opacity-30"
                style={{ background: "radial-gradient(ellipse at center, rgba(92,16,28,0.95), transparent 65%)" }} />
            <div className="absolute inset-0 jaali opacity-40" />
            <svg className="page-hero-arch absolute -right-24 top-10 h-[34rem] opacity-[0.16] hidden md:block" viewBox="0 0 200 260" fill="none" aria-hidden="true">
                <path d="M40 250 V110 Q40 20 100 20 Q160 20 160 110 V250" stroke="var(--gold)" strokeWidth="1" />
                <path d="M60 250 V115 Q60 42 100 42 Q140 42 140 115 V250" stroke="var(--gold)" strokeWidth="0.6" />
                <path d="M80 250 V120 Q80 64 100 64 Q120 64 120 120 V250" stroke="var(--gold)" strokeWidth="0.4" />
            </svg>
        </div>
        <GlowOrbs />
        <Particles density={30} />
        <div className="container-x relative">
            <Reveal>
                <span className="eyebrow">{eyebrow}</span>
                <h1 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.04] mt-6 max-w-4xl">
                    {title}
                </h1>
                {sub && <p className="mt-6 text-base md:text-lg text-muted-luxe leading-relaxed max-w-2xl">{sub}</p>}
                {children && <div className="mt-10 flex flex-wrap gap-4">{children}</div>}
            </Reveal>
        </div>
        <div className="divider-gold absolute bottom-0 inset-x-0" />
    </section>
);

const icsDate = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

export const AddToCalendar = ({ className = "" }) => {
    const download = () => {
        const ics = [
            "BEGIN:VCALENDAR",
            "VERSION:2.0",
            "PRODID:-//Startup MahaBazaar//2027//EN",
            "BEGIN:VEVENT",
            "UID:mahabazaar-2027@startupmahabazaar.com",
            `DTSTART:${icsDate(new Date("2027-01-29T09:00:00+05:30"))}`,
            `DTEND:${icsDate(new Date("2027-01-31T22:00:00+05:30"))}`,
            "SUMMARY:Startup MahaBazaar 2027 — Bazaar of Ideas, Innovation & Impact",
            "LOCATION:MSIT & MSI, New Delhi",
            "DESCRIPTION:India's Grand Festival of Startups, Innovation & Entrepreneurship.",
            "END:VEVENT",
            "END:VCALENDAR",
        ].join("\r\n");
        const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "startup-mahabazaar-2027.ics";
        a.click();
        URL.revokeObjectURL(url);
    };
    return (
        <button onClick={download} data-testid="add-to-calendar-btn"
            className={`inline-flex items-center gap-2 text-[0.64rem] tracking-[0.28em] uppercase text-[var(--champagne)]/80 hover:text-[var(--champagne)] transition-colors ${className}`}>
            <CalendarPlus size={14} strokeWidth={1.5} /> Add to Calendar
        </button>
    );
};

export const StatCell = ({ value, suffix, label }) => (
    <div className="text-center px-4 py-6 sm:py-8">
        <div className="font-display text-3xl sm:text-4xl gold-text font-semibold tabular-nums">
            <Counter to={value} suffix={suffix} />
        </div>
        <div className="mt-2 text-[0.6rem] tracking-[0.3em] uppercase text-muted-luxe">{label}</div>
    </div>
);
